import memoize from "memoize-one";
import {
  ComponentProps,
  CSSProperties,
  FC,
  forwardRef,
  memo,
  ReactNode,
  Ref,
  useImperativeHandle,
  useState,
} from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  Align,
  areEqual,
  CommonProps,
  FixedSizeGrid as Grid,
} from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

type BaseItem = { id: string };

type ItemData<ITEM extends BaseItem = { id: string }> = {
  items?: ITEM[];
  selectedItems?: ITEM[];
  disabledItems?: ITEM[];
  isItemLoaded: (index: number) => boolean;
  renderItem: (props: ItemProps<ITEM>) => ReactNode;
  columnCount: number;
  renderPlaceholders: boolean;
};

type ItemWrapperProps = {
  data: ItemData;
  index?: number;
  columnIndex?: number;
  rowIndex?: number;
  style: CSSProperties;
};

const ItemWrapper = memo(
  ({ data, columnIndex = 0, rowIndex = 0, style }: ItemWrapperProps) => {
    const {
      items,
      selectedItems,
      disabledItems,
      columnCount,
      renderPlaceholders,
      isItemLoaded,
      renderItem,
    } = data;

    const itemIndex = rowIndex * columnCount + columnIndex;

    if (!isItemLoaded(itemIndex) && renderPlaceholders)
      return renderItem({
        viewType: "grid",
        item: null,
        isSelected: false,
        isDisabled: false,
        isLoaded: false,
        style,
      });

    const item = items?.[itemIndex];
    if (!item) return null;

    const isSelected = selectedItems?.some((i) => i.id === item.id) ?? false;
    const isDisabled = disabledItems?.some((i) => i.id === item.id) ?? false;

    return renderItem({
      viewType: "grid",
      item,
      isSelected,
      isDisabled,
      isLoaded: true,
      style,
    });
  },
  areEqual,
);

type ItemProps<ITEM extends BaseItem> = {
  viewType: "grid";
  isSelected: boolean;
  isDisabled: boolean;
  style?: CSSProperties;
} & ({ isLoaded: true; item: ITEM } | { isLoaded: false; item: null });

type InfiniteScrollGridProps<ITEM extends BaseItem> = {
  gridListProps?: CommonProps;
  items?: ITEM[];
  selectedItems?: ITEM[];
  disabledItems?: ITEM[];
  renderItem: (props: ItemProps<ITEM>) => ReactNode;
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadNextPage:
    | ComponentProps<typeof InfiniteLoader>["loadMoreItems"]
    | (() => boolean);
  columnCount?: number;
  xSpacing?: number;
  ySpacing?: number;
  widthPadding?: number;
  renderPlaceholders?: boolean;
  thresholdRows?: number;
};

interface GridHandle {
  scrollToItem(index: number, align?: Align): void;
  scrollToGridItem(rowIndex: number, colIndex: number, align?: Align): void;
}

interface InfiniteScrollGridType extends FC<InfiniteScrollGridProps<BaseItem>> {
  <T extends BaseItem>(
    props: InfiniteScrollGridProps<T> & { ref?: Ref<GridHandle> },
  ): ReturnType<React.FC<InfiniteScrollGridProps<T>>>;
}

type GridRef = Grid<any> | null;

/**
 * When used as a direct child to a flex container, wrap with a div (see https://github.com/bvaughn/react-window/issues/249#issuecomment-739682222)
 * <Box
 *   sx={{
 *     width: "100%",
 *     display: "flex",
 *     flexDirection: "column",
 *     flexGrow: 1,
 *   }}
 * >
 *   <InfiniteScrollGrid ... />
 * </Box>
 */
export const InfiniteScrollGrid: InfiniteScrollGridType = forwardRef(
  (
    {
      gridListProps,
      items,
      selectedItems,
      disabledItems,
      renderItem,
      hasNextPage,
      isNextPageLoading,
      loadNextPage,
      columnCount,
      xSpacing = 0,
      ySpacing = 0,
      widthPadding = 0,
      renderPlaceholders,
      thresholdRows,
    },
    ref?: Ref<GridHandle>,
  ) => {
    const itemsArr = items ?? [];
    const colCount = columnCount ?? 1;

    // If there are more items to be loaded then add an extra row to hold a loading indicator.
    const itemCount = hasNextPage
      ? itemsArr.length + colCount
      : itemsArr.length;

    // Only load 1 page of items at a time.
    // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
    const loadMoreItems = isNextPageLoading
      ? () => {}
      : (startIndex: number, stopIndex: number) => {
          loadNextPage(startIndex, stopIndex);
        };

    // Every row is loaded except for our loading indicator row.
    const isItemLoaded = (index: number) =>
      !hasNextPage || index < itemsArr.length;

    const [gridRef, setGridRef] = useState<GridRef>(null);

    useImperativeHandle(ref, () => ({
      scrollToItem(index: number, align?: Align) {
        if (gridRef) {
          const columnIndex = index % colCount;
          const rowIndex = Math.floor(index / colCount);
          gridRef.scrollToItem({
            align,
            columnIndex,
            rowIndex,
          });
        }
      },
      scrollToGridItem(rowIndex: number, columnIndex: number, align?: Align) {
        if (gridRef) {
          gridRef.scrollToItem({
            align,
            columnIndex,
            rowIndex,
          });
        }
      },
    }));

    const createItemData = memoize(
      (
        itemsList: BaseItem[] | undefined,
        selectedItemsList: BaseItem[] | undefined,
        disabledItemsList: BaseItem[] | undefined,
        renderItemFn: (props: ItemProps<BaseItem>) => ReactNode,
        isItemLoadedFn: (index: number) => boolean,
        columnCountNum: number,
        renderPlaceholdersFlag: boolean | undefined,
      ): ItemData => ({
        items: itemsList,
        selectedItems: selectedItemsList,
        disabledItems: disabledItemsList,
        renderItem: renderItemFn,
        isItemLoaded: isItemLoadedFn,
        columnCount: columnCountNum,
        renderPlaceholders: renderPlaceholdersFlag ?? false,
      }),
    );

    const itemData = createItemData(
      items,
      selectedItems,
      disabledItems,
      renderItem,
      isItemLoaded,
      colCount,
      renderPlaceholders,
    );

    if (!colCount) {
      throw new Error("columnCount must be > 0");
    }

    return (
      <AutoSizer>
        {({ height, width }) => {
          const rowCount = Math.ceil(itemCount / colCount);
          const rowHeight =
            (width - xSpacing * (colCount - 1)) / colCount + ySpacing;
          // -1 because <FixedSizeGrid/> uses full padding to fill gap between `width` and `rowCount * columnWidth`
          const columnWidth = (width - xSpacing * (colCount - 1)) / colCount;

          return (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={loadMoreItems}
              threshold={(thresholdRows ?? 4) * colCount}
            >
              {({ onItemsRendered, ref: loaderRef }) => (
                <Grid
                  itemData={itemData}
                  width={width + widthPadding}
                  height={height}
                  columnCount={colCount}
                  rowCount={rowCount}
                  columnWidth={columnWidth}
                  rowHeight={rowHeight}
                  onItemsRendered={(gridProps) => {
                    onItemsRendered({
                      overscanStartIndex:
                        gridProps.overscanRowStartIndex * colCount,
                      overscanStopIndex:
                        gridProps.overscanRowStopIndex * colCount,
                      visibleStartIndex:
                        gridProps.visibleRowStartIndex * colCount,
                      visibleStopIndex:
                        gridProps.visibleRowStopIndex * colCount,
                    });
                  }}
                  {...gridListProps}
                  ref={(grid) => {
                    loaderRef(grid); // Give InfiniteLoader a reference to the list
                    setGridRef(grid); // Set our own ref to it as well.
                  }}
                >
                  {ItemWrapper}
                </Grid>
              )}
            </InfiniteLoader>
          );
        }}
      </AutoSizer>
    );
  },
);
